import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ASSET_STATUS_LABELS, ASSET_STATUSES, TAX_RATES, assetValue, berlinDate, formatCents, type Asset, type AssetLogEntry, type AssetStatus, type ChartOption, type DocumentItem, type EmployeeList, type Paginated } from '@kontor/shared';
import { api, apiUpload, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { centsText, parseCents } from '../../money';
import { useSession } from '../../session';
import { RecordDocuments } from '../../components/RecordDocuments';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { LocationSelect, dateTimeFormat, errorText, formatDay, useLocations } from './shared';
import './inventory.css';

/** Antwort beim Erfassen der Kaufrechnung (wie beim Beleg zu Fahrzeugkosten) */
interface ReceiptResult { invoiceId: string; number: string; status: 'draft' | 'approved'; paid: boolean; notes: string[] }

const WARRANTY_LABELS = { valid: 'Garantie läuft', expiring: 'Garantie läuft bald ab', expired: 'Garantie abgelaufen' } as const;

function useEmployees() {
  const { can, hasModule } = useSession();
  return useQuery({
    queryKey: ['employees', 'inventory-options'],
    queryFn: async () => (await api<EmployeeList>('GET', '/employees?status=active')).data,
    enabled: hasModule('hr') && can('hr.read'),
    staleTime: 60_000,
  });
}

interface Draft {
  name: string;
  category: string;
  serialNumber: string;
  manufacturer: string;
  model: string;
  acquiredOn: string;
  cost: string;
  taxRate: string;
  years: string;
  warrantyUntil: string;
  locationId: string;
  employeeId: string;
  status: AssetStatus;
  note: string;
}
const draftOf = (a?: Asset): Draft => ({
  name: a?.name ?? '',
  category: a?.category ?? '',
  serialNumber: a?.serialNumber ?? '',
  manufacturer: a?.manufacturer ?? '',
  model: a?.model ?? '',
  acquiredOn: a?.acquiredOn ?? '',
  cost: a?.acquisitionGrossCents != null ? centsText(a.acquisitionGrossCents) : '',
  taxRate: String(a?.taxRate ?? 19),
  years: a?.depreciationYears != null ? String(a.depreciationYears) : '',
  warrantyUntil: a?.warrantyUntil ?? '',
  locationId: a?.locationId ?? '',
  employeeId: a?.employeeId ?? '',
  status: a?.status ?? 'active',
  note: a?.note ?? '',
});

/** Formular für Inventar. Anschaffungskosten zeigt und ändert nur, wer das Recht „Werte sehen“ hat. */
function AssetForm({ draft, setDraft, asset, onSubmit, pending, error, onCancel, submitLabel }: { draft: Draft; setDraft: (d: Draft) => void; asset?: Asset; onSubmit: () => void; pending: boolean; error: string | null; onCancel: () => void; submitLabel: string }) {
  const { can } = useSession();
  const employees = useEmployees();
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };
  return (
    <form className="card inv-booking" onSubmit={submit} aria-label="Inventar">
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field field--wide"><span>Bezeichnung</span><input className="input" value={draft.name} onChange={(e) => set({ name: e.target.value })} maxLength={150} required autoFocus /></label>
        <label className="field"><span>Kategorie</span><input className="input" value={draft.category} onChange={(e) => set({ category: e.target.value })} maxLength={60} placeholder="Werkzeug, Fahrzeug, IT …" /></label>
        <label className="field"><span>Seriennummer</span><input className="input" value={draft.serialNumber} onChange={(e) => set({ serialNumber: e.target.value })} maxLength={80} /></label>
        <label className="field"><span>Hersteller</span><input className="input" value={draft.manufacturer} onChange={(e) => set({ manufacturer: e.target.value })} maxLength={100} /></label>
        <label className="field"><span>Modell</span><input className="input" value={draft.model} onChange={(e) => set({ model: e.target.value })} maxLength={100} /></label>
        <label className="field"><span>Angeschafft am</span><input className="input" type="date" value={draft.acquiredOn} onChange={(e) => set({ acquiredOn: e.target.value })} /></label>
        {can('inventory.values') && (
          <>
            <label className="field"><span>Anschaffungskosten brutto (€)</span><input className="input" inputMode="decimal" value={draft.cost} onChange={(e) => set({ cost: e.target.value })} placeholder="595,00" /></label>
            <label className="field">
              <span>Steuersatz</span>
              <select className="input" value={draft.taxRate} onChange={(e) => set({ taxRate: e.target.value })}>
                {TAX_RATES.map((r) => <option key={r} value={r}>{r} %</option>)}
              </select>
            </label>
            <label className="field">
              <span>Nutzungsdauer (Jahre)</span>
              <input className="input" type="number" min={1} max={50} step={1} value={draft.years} onChange={(e) => set({ years: e.target.value })} placeholder="zum Beispiel 5" />
            </label>
            <ValuePreview draft={draft} />
          </>
        )}
        <label className="field"><span>Garantie bis</span><input className="input" type="date" value={draft.warrantyUntil} onChange={(e) => set({ warrantyUntil: e.target.value })} /></label>
        <LocationSelect value={draft.locationId} onChange={(id) => set({ locationId: id })} label="Standort" allowEmpty emptyLabel="Kein Standort" />
        <label className="field">
          <span>Verantwortlich</span>
          <select className="input" value={draft.employeeId} onChange={(e) => set({ employeeId: e.target.value })} disabled={asset?.status === 'retired'}>
            <option value="">Niemand</option>
            {asset?.employeeId && !employees.data?.some((e) => e.id === asset.employeeId) && <option value={asset.employeeId}>{asset.employeeName ?? 'Mitarbeiter'}</option>}
            {employees.data?.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select className="input" value={draft.status} onChange={(e) => set({ status: e.target.value as AssetStatus })}>
            {ASSET_STATUSES.filter((s) => s !== 'retired' || asset?.status === 'retired').map((s) => <option key={s} value={s}>{ASSET_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="field field--wide"><span>Notiz</span><textarea className="input textarea textarea--short" value={draft.note} onChange={(e) => set({ note: e.target.value })} maxLength={2000} /></label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={pending}>{pending ? 'Wird gespeichert …' : submitLabel}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}

/** Netto, Steuer und Restwert, während man tippt */
function ValuePreview({ draft }: { draft: Draft }) {
  const gross = draft.cost.trim() ? parseCents(draft.cost) : null;
  const years = draft.years.trim() ? Number(draft.years) : null;
  const v = assetValue({ grossCents: gross, taxRate: Number(draft.taxRate), acquiredOn: draft.acquiredOn || null, years: years && years >= 1 ? years : null, asOf: berlinDate() });
  if (v.netCents === null) return null;
  return (
    <p className="muted small field--wide inv-preview" aria-live="polite">
      Netto {formatCents(v.netCents)} + Steuer {formatCents(v.taxCents ?? 0)} = brutto {formatCents(gross ?? 0)}.
      {v.bookValueCents !== null ? ` Restwert heute nach linearer Abschreibung: ${formatCents(v.bookValueCents)}.` : ' Für den Restwert fehlen Anschaffungsdatum und Nutzungsdauer.'}
    </p>
  );
}

function bodyOf(d: Draft, withCost: boolean): Record<string, unknown> {
  const cost = d.cost.trim() ? parseCents(d.cost) : null;
  if (withCost && d.cost.trim() && cost === null) throw new Error('Bitte geben Sie die Anschaffungskosten als Betrag ein, zum Beispiel 129,00.');
  return {
    name: d.name.trim(),
    category: d.category.trim() || null,
    serialNumber: d.serialNumber.trim() || null,
    manufacturer: d.manufacturer.trim() || null,
    model: d.model.trim() || null,
    acquiredOn: d.acquiredOn || null,
    ...(withCost ? { acquisitionGrossCents: cost, taxRate: cost === null ? null : Number(d.taxRate), depreciationYears: d.years.trim() ? Number(d.years) : null } : {}),
    warrantyUntil: d.warrantyUntil || null,
    locationId: d.locationId || null,
    employeeId: d.employeeId || null,
    ...(d.status === 'retired' ? {} : { status: d.status }),
    note: d.note.trim() || null,
  };
}

/** Inventar (KON-166): Betriebsmittel mit Inventarnummer, Standort und Verantwortlichem */
export function AssetsPage() {
  const { can, areaHidden } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const locationId = params.get('locationId') ?? '';
  const warranty = params.get('warranty') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locations = useLocations();
  const list = useQuery({
    queryKey: ['inventory-assets', { q, status, locationId, warranty, sort, page }],
    queryFn: () => api<Paginated<Asset>>('GET', `/inventory/assets${queryString({ q, status, locationId, warranty, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'status', label: 'Status', type: 'select', options: [{ value: '', label: 'Alle' }, ...ASSET_STATUSES.map((s) => ({ value: s, label: ASSET_STATUS_LABELS[s] }))] },
    { key: 'locationId', label: 'Standort', type: 'select', options: [{ value: '', label: 'Alle' }, ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.path }))] },
    { key: 'warranty', label: 'Garantie', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'expiring', label: 'Läuft in 60 Tagen ab' }, { value: 'expired', label: 'Abgelaufen' }] },
  ];
  const create = useMutation({
    mutationFn: () => api<{ data: Asset }>('POST', '/inventory/assets', bodyOf(draft!, can('inventory.values'))),
    onSuccess: (r) => {
      client.invalidateQueries({ queryKey: ['inventory-assets'] });
      client.invalidateQueries({ queryKey: ['inventory-overview'] });
      navigate(`/inventar/${r.data.id}`);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Anlegen hat nicht geklappt.')),
  });
  return (
    <>
      <div className="page-head">
        <h1>Inventar</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('inventory.write') && <button type="button" className="btn btn--primary" onClick={() => { setError(null); setDraft(draftOf()); }}>Neues Inventar</button>}
      </div>
      <p className="muted">{!areaHidden('/lager') && <><Link to="/lager">← Lager</Link> · </>}Geräte, Werkzeuge und Ausstattung Ihres Betriebs.{can('inventory.values') && <> Fahrzeuge führen Sie unter Flotte.{areaHidden('/inventar/anlagenspiegel') ? ' Der Anlagenspiegel zeigt beides zusammen mit Restwerten.' : <> <Link to="/inventar/anlagenspiegel">Anlagenspiegel</Link> zeigt beides zusammen mit Restwerten.</>}</>}</p>
      {draft && <AssetForm draft={draft} setDraft={setDraft} onSubmit={() => { setError(null); try { bodyOf(draft, can('inventory.values')); } catch (e) { setError((e as Error).message); return; } create.mutate(); }} pending={create.isPending} error={error} onCancel={() => setDraft(null)} submitLabel="Anlegen" />}
      <FilterBar search={{ value: q, placeholder: 'Nummer, Bezeichnung, Seriennummer oder Verantwortlicher', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ status, locationId, warranty }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Nummer" sortKey="number" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Bezeichnung" sortKey="name" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Kategorie" sortKey="category" sort={sort} onSort={(s) => update({ sort: s })} />
              <th>Standort</th>
              <th>Verantwortlich</th>
              <SortTh label="Status" sortKey="status" sort={sort} onSort={(s) => update({ sort: s })} />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={7} className="empty">{q || status || locationId || warranty ? 'Kein Inventar zu dieser Suche.' : 'Noch kein Inventar erfasst.'}</td></tr>}
            {list.data?.data.map((a) => (
              <ExpandRow
                key={a.id}
                className={a.status === 'retired' ? 'inv-reversed' : ''}
                summary={<Summary title={<Link to={`/inventar/${a.id}`} className="strong-link">{a.name}</Link>} aside={<span className="doc-kind">{ASSET_STATUS_LABELS[a.status]}</span>} line2={`${a.number}${a.category ? ` · ${a.category}` : ''}`} line3={[a.locationPath, a.employeeName].filter(Boolean).join(' · ') || undefined} />}
              >
                <td className="cell-desktop-title"><Link to={`/inventar/${a.id}`} className="strong-link">{a.number}</Link></td>
                <td data-label="Bezeichnung">{a.name}{a.serialNumber && <div className="muted small">SN {a.serialNumber}</div>}</td>
                <td data-label="Kategorie">{a.category ?? '–'}</td>
                <td data-label="Standort">{a.locationPath ?? '–'}</td>
                <td data-label="Verantwortlich">{a.employeeName ?? '–'}</td>
                <td data-label="Status"><span className="doc-kind">{ASSET_STATUS_LABELS[a.status]}</span>{a.warrantyState && a.warrantyState !== 'valid' && a.status !== 'retired' && <div className={`small ${a.warrantyState === 'expired' ? 'inv-bad' : 'inv-warn'}`}>{WARRANTY_LABELS[a.warrantyState]}</div>}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}

/** Ein Inventarstück (KON-166): Daten, Bearbeiten, Ausmustern und Verlauf */
export function AssetDetailPage() {
  const { id = '' } = useParams();
  const { can, areaHidden } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const asset = useQuery({ queryKey: ['inventory-asset', id], queryFn: async () => (await api<{ data: Asset }>('GET', `/inventory/assets/${id}`)).data });
  const log = useQuery({ queryKey: ['inventory-asset-log', id], queryFn: async () => (await api<{ data: AssetLogEntry[] }>('GET', `/inventory/assets/${id}/log`)).data });
  const refresh = () => {
    for (const key of ['inventory-asset', 'inventory-asset-log', 'inventory-assets', 'inventory-overview', 'inventory-locations']) client.invalidateQueries({ queryKey: [key] });
  };
  const save = useMutation({
    mutationFn: () => api('PATCH', `/inventory/assets/${id}`, bodyOf(draft!, can('inventory.values'))),
    onSuccess: () => { refresh(); setDraft(null); setMessage({ ok: true, text: 'Die Änderungen sind gespeichert.' }); },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Speichern hat nicht geklappt.')),
  });
  const retire = useMutation({
    mutationFn: (reason: string) => api('POST', `/inventory/assets/${id}/retire`, { reason }),
    onSuccess: () => { refresh(); setMessage({ ok: true, text: 'Das Inventar ist ausgemustert.' }); },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Ausmustern hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/inventory/assets/${id}`),
    onSuccess: () => { refresh(); navigate('/inventar'); },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });
  const a = asset.data;
  if (asset.isError) return <div className="alert alert--error">{errorText(asset.error, 'Das Inventar konnte nicht geladen werden.')}</div>;
  if (!a) return <p className="muted">Wird geladen …</p>;
  const mayWrite = can('inventory.write');
  const row = (label: string, value: string | null) => (value ? <><dt>{label}</dt><dd>{value}</dd></> : null);

  return (
    <>
      <div className="page-head"><h1>{a.name}</h1><span className="doc-kind">{ASSET_STATUS_LABELS[a.status]}</span></div>
      <p className="muted">{!areaHidden('/inventar') && <><Link to="/inventar">← Alle Inventar</Link> · </>}{a.number}</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {draft ? (
        <AssetForm draft={draft} setDraft={setDraft} asset={a} onSubmit={() => { setError(null); try { bodyOf(draft, can('inventory.values')); } catch (e) { setError((e as Error).message); return; } save.mutate(); }} pending={save.isPending} error={error} onCancel={() => setDraft(null)} submitLabel="Speichern" />
      ) : (
        <section className="card">
          <dl className="inv-facts">
            {row('Kategorie', a.category)}
            {row('Seriennummer', a.serialNumber)}
            {row('Hersteller', a.manufacturer)}
            {row('Modell', a.model)}
            {row('Standort', a.locationPath)}
            {row('Verantwortlich', a.employeeName)}
            {row('Angeschafft am', a.acquiredOn ? formatDay(a.acquiredOn) : null)}
            {row('Anschaffungskosten', a.acquisitionGrossCents !== null ? `${formatCents(a.acquisitionGrossCents)} brutto (netto ${formatCents(a.acquisitionNetCents ?? 0)} + ${a.taxRate} % Steuer ${formatCents(a.acquisitionTaxCents ?? 0)})` : null)}
            {row('Abschreibung', a.depreciationYears ? `linear über ${a.depreciationYears} Jahre${a.depreciatedCents !== null ? `, bisher ${formatCents(a.depreciatedCents)}` : ''}` : null)}
            {row('Restwert heute', a.bookValueCents !== null ? formatCents(a.bookValueCents) : null)}
            {row('Garantie bis', a.warrantyUntil ? `${formatDay(a.warrantyUntil)}${a.warrantyState && a.warrantyState !== 'valid' ? ` (${WARRANTY_LABELS[a.warrantyState]})` : ''}` : null)}
            {row('Ausgemustert am', a.retiredOn ? `${formatDay(a.retiredOn)}: ${a.retirementReason ?? ''}` : null)}
            {row('Notiz', a.note)}
          </dl>
          {mayWrite && (
            <div className="form-actions">
              <button type="button" className="btn btn--primary" onClick={() => { setError(null); setMessage(null); setDraft(draftOf(a)); }}>{a.status === 'retired' ? 'Wieder in Betrieb nehmen' : 'Bearbeiten'}</button>
              {a.status !== 'retired' && <button type="button" className="btn btn--secondary" onClick={() => { const reason = window.prompt('Grund der Ausmusterung:'); if (reason?.trim()) retire.mutate(reason.trim()); }}>Ausmustern</button>}
              {a.status !== 'retired' && can('inventory.delete') && <button type="button" className="btn btn--danger" onClick={() => window.confirm('Dieses Inventar löschen? Besser ausmustern, dann bleibt der Nachweis erhalten.') && remove.mutate()}>Löschen</button>}
            </div>
          )}
        </section>
      )}
      <AssetDocuments assetId={a.id} />
      <AssetReceipt asset={a} onDone={(text) => setMessage({ ok: true, text })} />
      <CollapsibleCard title="Verlauf" storageKey="asset-log">
      <ul className="inv-list">
        {log.data?.map((l) => <li key={l.id}>{dateTimeFormat.format(new Date(l.at))} · {l.text}{l.actorName ? ` von ${l.actorName}` : ''}</li>)}
        {log.data?.length === 0 && <li className="muted">Kein Eintrag.</li>}
      </ul>
      </CollapsibleCard>
    </>
  );
}

/** Dokumente zum Inventarstück (Kaufrechnung, Garantie, Anleitung), siehe RecordDocuments */
function AssetDocuments({ assetId }: { assetId: string }) {
  return <RecordDocuments referenceKind="asset" referenceId={assetId} hint="Kaufrechnung, Garantie und Anleitung." />;
}

const PAID_OPTIONS: [string, string][] = [['', 'Noch offen'], ['bank_transfer', 'Überweisung'], ['card', 'Karte'], ['cash', 'Bar'], ['paypal', 'PayPal']];
const NEW_FILE = 'neu';

/**
 * Kaufrechnung als Eingangsrechnung: Aus Anschaffungsdatum, Kosten und Steuersatz des Inventarstücks entsteht eine Eingangsrechnung mit einer Position, dem Beleg als Original
 * und, wenn gewählt, gleich dem Sachkonto. Der Beleg kommt aus den Dokumenten des Inventarstücks oder als neue Datei. Danach steht hier der Weg zur Eingangsrechnung.
 */
function AssetReceipt({ asset: a, onDone }: { asset: Asset; onDone: (text: string) => void }) {
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const allowed = hasModule('invoices_in') && can('invoices_in.write') && can('inventory.write') && can('inventory.values');
  const docs = useQuery({
    queryKey: ['documents', { referenceKind: 'asset', referenceId: a.id }],
    queryFn: async () => (await api<Paginated<DocumentItem>>('GET', `/documents${queryString({ referenceKind: 'asset', referenceId: a.id, perPage: 100, sort: '-createdAt' })}`)).data,
    enabled: allowed && !a.purchaseInvoiceId && hasModule('documents') && can('documents.read'),
  });
  const chart = useQuery({ queryKey: ['chart-options'], queryFn: async () => (await api<{ data: ChartOption[] }>('GET', '/chart/options')).data, enabled: allowed && !a.purchaseInvoiceId && hasModule('chart') && can('chart.read') });
  const invoiceDocs = (docs.data ?? []).filter((d) => d.category === 'invoice');
  const [source, setSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [supplier, setSupplier] = useState('');
  const [number, setNumber] = useState('');
  const [ledgerAccount, setLedgerAccount] = useState('');
  const [paid, setPaid] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Vorbelegung: die jüngste Rechnung aus den Dokumenten, sonst eine neue Datei
  const chosen = source || invoiceDocs[0]?.id || NEW_FILE;
  const accounts = (chart.data ?? []).filter((o) => o.kind === 'asset' || o.kind === 'expense').sort((x, y) => Number(y.kind === 'asset') - Number(x.kind === 'asset') || x.number.localeCompare(y.number));

  const create = useMutation({
    mutationFn: async () => {
      const query = queryString({ supplier: supplier.trim(), number: number.trim(), paid, ledgerAccount, documentId: chosen === NEW_FILE ? undefined : chosen });
      const path = `/inventory/assets/${a.id}/receipt${query}`;
      if (chosen === NEW_FILE) {
        if (!file) throw new Error('Bitte wählen Sie die Datei mit der Rechnung.');
        return (await apiUpload<{ data: ReceiptResult }>(path, new File([file], file.name, { type: 'application/octet-stream' }))).data;
      }
      return (await api<{ data: ReceiptResult }>('POST', path)).data;
    },
    onSuccess: (r) => {
      for (const key of ['inventory-asset', 'inventory-asset-log', 'purchase-invoices']) client.invalidateQueries({ queryKey: [key] });
      onDone(`Die Kaufrechnung ${r.number} steht als ${r.status === 'approved' ? 'freigegebene' : 'Entwurf einer'} Eingangsrechnung bereit${r.paid ? ' und ist als bezahlt vermerkt' : ''}.${r.notes.length > 0 ? ` ${r.notes.join(' ')}` : ''}`);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Die Eingangsrechnung konnte nicht angelegt werden.')),
  });

  if (!allowed) return null;
  if (a.purchaseInvoiceId) {
    return (
      <section className="card">
        <h2>Kaufrechnung</h2>
        <p>Die Kaufrechnung steht in den Eingangsrechnungen. Dort sehen Sie Kontierung, Freigabe und Zahlung.</p>
        <Link className="btn btn--secondary" to={`/eingangsrechnungen/${a.purchaseInvoiceId}`}>Eingangsrechnung öffnen</Link>
      </section>
    );
  }
  const ready = !!a.acquiredOn && (a.acquisitionGrossCents ?? 0) > 0 && a.taxRate !== null;
  return (
    <section className="card">
      <h2>Kaufrechnung als Eingangsrechnung</h2>
      <p className="muted small">Wirkia legt die Eingangsrechnung mit einer Position für dieses Inventar an: {a.name}{a.acquisitionGrossCents ? `, ${formatCents(a.acquisitionGrossCents)} brutto mit ${a.taxRate} % Steuer` : ''}{a.acquiredOn ? `, vom ${formatDay(a.acquiredOn)}` : ''}. Mit Sachkonto ist sie gleich kontiert und muss nicht mehr in den Eingangsrechnungen nachgetragen werden.</p>
      {!ready ? (
        <p className="alert">Tragen Sie zuerst Anschaffungsdatum, Anschaffungskosten und Steuersatz ein und speichern Sie, dann lässt sich die Rechnung erfassen.</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!supplier.trim()) return setError('Bitte nennen Sie den Lieferanten.');
            create.mutate();
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span>Beleg</span>
              <select className="input" value={chosen} onChange={(e) => setSource(e.target.value)}>
                {invoiceDocs.map((d) => <option key={d.id} value={d.id}>{d.title ?? d.fileName} (aus den Dokumenten)</option>)}
                <option value={NEW_FILE}>Neue Datei wählen …</option>
              </select>
            </label>
            {chosen === NEW_FILE && (
              <label className="field">
                <span>Datei</span>
                <input className="input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xml" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
            )}
            <label className="field">
              <span>Lieferant</span>
              <input className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={200} placeholder={a.manufacturer ? `zum Beispiel der Händler, nicht unbedingt ${a.manufacturer}` : 'Händler oder Hersteller'} required />
            </label>
            <label className="field">
              <span>Rechnungsnummer (freiwillig)</span>
              <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={100} />
            </label>
            <label className="field">
              <span>Sachkonto</span>
              {accounts.length > 0 ? (
                <select className="input" value={ledgerAccount} onChange={(e) => setLedgerAccount(e.target.value)}>
                  <option value="">Später in den Eingangsrechnungen wählen</option>
                  {accounts.map((o) => <option key={o.number} value={o.number}>{o.number} · {o.name}</option>)}
                </select>
              ) : (
                <input className="input num" inputMode="numeric" value={ledgerAccount} onChange={(e) => setLedgerAccount(e.target.value.replace(/\D/g, ''))} maxLength={8} placeholder="zum Beispiel 0480" />
              )}
            </label>
            <label className="field">
              <span>Bezahlt</span>
              <select className="input" value={paid} onChange={(e) => setPaid(e.target.value)}>
                {PAID_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          {error && <div className="alert alert--error" role="alert">{error}</div>}
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={create.isPending}>{create.isPending ? 'Wird angelegt …' : 'Als Eingangsrechnung anlegen'}</button>
          </div>
        </form>
      )}
    </section>
  );
}
